import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { usePartnerMembership } from "@/hooks/usePartnerMembership";
import {
  RoleType,
  PermissionLevel,
  ROLE_DISPLAY_NAMES,
} from "@shared/permissions";
import { dispatchAuthExpired } from "@/lib/queryClient";
import PermissionsDegradedBanner from "@/components/PermissionsDegradedBanner";
import { decidePermissionGateOutcome } from "@/components/requirePermissionState";

interface RequirePermissionProps {
  component: React.ComponentType;
  role: RoleType;
  minLevel?: PermissionLevel;
  /**
   * Task #2623 — when set, a user who lacks the Eatzy `role` above is still
   * granted access if they have at least one active `restaurant_partners`
   * membership. Used by the partner-portal routes so freshly invited
   * staff/managers/owners aren't met with "Access Denied" right after they
   * accept their invitation.
   */
  allowPartnerMembership?: boolean;
}

export default function RequirePermission({
  component: Component,
  role,
  minLevel,
  allowPartnerMembership = false,
}: RequirePermissionProps) {
  const { isAuthenticated, isLoading: authLoading, token } = useAuth();
  const {
    hasRole,
    hasPermission,
    isPending: permPending,
    isFetching: permFetching,
    isError,
    error,
    refetch,
    isUsingCache,
  } = usePermissions();
  const [location, setLocation] = useLocation();

  const hasAccess = minLevel ? hasPermission(role, minLevel) : hasRole(role);

  // Task #2623 — partner-portal fallback. Only probe partner membership when
  // this gate opts in AND the Eatzy role check has already missed, so admin /
  // non-partner gates never fire the extra request.
  const { hasMembership, isLoading: partnerMembershipLoading } =
    usePartnerMembership(allowPartnerMembership && !hasAccess);

  // The full state-machine decision is a pure function so it can be
  // exhaustively unit-tested without React rendering infrastructure.
  // See client/src/components/requirePermissionState.ts and
  // server/tests/requirePermissionState.test.ts.
  const outcome = decidePermissionGateOutcome({
    authLoading,
    isAuthenticated,
    permPending,
    permIsError: isError,
    permError: error,
    isUsingCache,
    hasAccess,
    allowPartnerMembership,
    partnerMembershipLoading,
    hasPartnerMembership: hasMembership,
  });

  useEffect(() => {
    if (outcome.kind === "redirect-to-login") {
      const returnUrl = encodeURIComponent(location);
      setLocation(`/login?returnUrl=${returnUrl}`);
    }
  }, [outcome.kind, location, setLocation]);

  // Safety net: if we're authenticated but the permissions query is stuck
  // in a "pending but not fetching" state (no data, no active request, no
  // error), kick off a fresh fetch. This recovers from the race where
  // removeQueries was called mid-flight and left the observer stranded.
  useEffect(() => {
    if (
      isAuthenticated &&
      !!token &&
      permPending &&
      !permFetching &&
      !isError
    ) {
      refetch();
    }
  }, [isAuthenticated, token, permPending, permFetching, isError, refetch]);

  useEffect(() => {
    // Use the shared dispatcher so this is deduped against any
    // auth:expired event the queryClient layer may have already emitted
    // for the same failed request (single-toast guarantee).
    if (outcome.kind === "force-logout") dispatchAuthExpired();
  }, [outcome.kind]);

  switch (outcome.kind) {
    case "loading":
    case "redirect-to-login":
    case "force-logout":
      return (
        <div
          className="min-h-screen flex flex-col items-center justify-center gap-3"
          data-testid="require-permission-loading"
        >
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Checking permissions...
          </p>
        </div>
      );

    case "access-denied":
      return (
        <div
          className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center"
          data-testid="require-permission-denied"
        >
          <div className="text-4xl font-bold text-muted-foreground">
            Access Denied
          </div>
          <p className="text-muted-foreground max-w-md">
            You don't have access to the {ROLE_DISPLAY_NAMES[role] || role}{" "}
            portal. Contact your administrator for access.
          </p>
        </div>
      );

    case "render-with-cache-banner":
      return (
        <>
          <PermissionsDegradedBanner />
          <Component />
        </>
      );

    case "render":
      return <Component />;
  }
}
